import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { OperatorAuditEntry, OperatorTenant } from '@forecastify/shared';
import { Icon, SKELETON_WIDTHS, Skeleton, buttonClasses, skeletonWidth } from '@forecastify/ui';
import { AUDIT_ACTION_LABEL, labelFor } from '../audit-labels';
import { useI18n, useT } from '../i18n';
import { AUDIT_PATH } from '../routes';
import { formatDateTime } from '../chrome/format';
import { BTN_SM } from '../chrome/tones';
import { describeChange, usePlanNames } from './audit-change';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ *Napló* — the last few things operators did to this organisation.         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **An extract, with a way into the whole log.** The full trail filters by this organisation's
 * id, so *Teljes napló* lands on exactly these rows and their history.
 *
 * ⚠️ **`null` is loading, `[]` is "nothing has happened", `'failed'` is failed** — three states,
 * because on a log, empty is a claim.
 */
export function TenantAuditTab({
  tenant,
  trail,
}: {
  readonly tenant: OperatorTenant;
  readonly trail: readonly OperatorAuditEntry[] | 'failed' | null;
}): ReactNode {
  const t = useT();
  const { locale } = useI18n();
  const planNames = usePlanNames();

  return (
    <section aria-label={t('tenant.audit')} className="flex flex-col gap-2">
      <div className="flex items-center">
        <h3 className="text-[14px] font-medium">{t('tenant.auditLede')}</h3>
        <Link
          to={`${AUDIT_PATH}?resourceType=tenant&resourceId=${encodeURIComponent(tenant.id)}`}
          className={buttonClasses('ghost', 'sm', `${BTN_SM} ml-auto text-foreground`)}
        >
          {t('tenant.auditAll')}
          <Icon name="arrow-right" size={13} />
        </Link>
      </div>
      <div className="flex flex-col rounded-lg border">
        {trail === null ? (
          <>
            {SKELETON_WIDTHS.slice(0, 5).map((_widths, row) => (
              <div
                key={`skeleton-${String(row)}`}
                aria-hidden
                className="flex h-[42px] items-center gap-x-3 border-b px-3.5 last:border-0"
              >
                <div className="w-[120px] shrink-0">
                  <Skeleton width={skeletonWidth(row, 0)} />
                </div>
                <Skeleton width="96px" />
                <div className="min-w-0 flex-1">
                  <Skeleton width={skeletonWidth(row, 2)} />
                </div>
              </div>
            ))}
            <p role="status" className="sr-only">
              {t('tenants.loading')}
            </p>
          </>
        ) : trail === 'failed' ? (
          <p role="alert" className="text-muted-foreground px-4 py-6 text-center text-[12.5px]">
            {t('tenant.auditFailed')}
          </p>
        ) : trail.length === 0 ? (
          <p className="text-muted-foreground px-4 py-6 text-center text-[12.5px]">
            {t('tenant.auditEmpty')}
          </p>
        ) : (
          trail.map((entry) => (
            <div
              key={entry.id}
              className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 border-b px-3.5 py-2.5 last:border-0"
            >
              <span className="text-muted-foreground w-[120px] shrink-0 text-[12px] tabular-nums">
                {formatDateTime(entry.createdAt, locale, {
                  today: t('when.today'),
                  yesterday: t('when.yesterday'),
                })}
              </span>
              <span className="text-[13px] font-medium">
                {labelFor(AUDIT_ACTION_LABEL, entry.action, t)}
              </span>
              <span className="text-muted-foreground min-w-0 flex-1 truncate text-[12.5px]">
                {describeChange(entry, t('audit.removed'), planNames)}
              </span>
              {/* ⚠️ `system` is a word, not a blank cell — the actor is an enum so "the clock did
                  it" and "somebody forgot to record who" cannot look alike. */}
              <span className="text-muted-foreground text-[12px]">
                {entry.actor === 'system'
                  ? t('audit.actor.system')
                  : (entry.actorName ?? entry.actorEmail ?? t('audit.actor.unknown'))}
              </span>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
